import * as React from "react";

import { useStateWithLocalStorage } from "@/hooks/useStateWithLocalStorage";
import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import * as People from "@/models/people";
import { useCompanyLoaderData } from "@/routes/useCompanyLoaderData";
import { IconX, RichContent } from "turboui";

const STORAGE_NAMESPACE = "announcements";
const STORAGE_KEY = "dismissed-site-message-ids";

function MessageDescription({ description }: { description: string }) {
  const { mentionedPersonLookup } = useRichEditorHandlers({ scope: People.NoneSearchScope });

  return (
    <RichContent
      content={description}
      mentionedPersonLookup={mentionedPersonLookup}
      parseContent
      // RichContent renders block wrappers; contents + inline <p> lets the description
      // flow after the bold title as one paragraph instead of a separate column.
      className="contents text-yellow-900 [&_div]:contents [&_p]:inline [&_p]:m-0 [&_a]:text-yellow-950 [&_a]:underline"
    />
  );
}

export function SiteMessageBanner() {
  const { siteMessages } = useCompanyLoaderData();
  const [dismissedIds, setDismissedIds] = useStateWithLocalStorage<string[]>(STORAGE_NAMESPACE, STORAGE_KEY, []);

  const visibleMessage = siteMessages.find((message) => message.id && !dismissedIds.includes(message.id));

  if (!visibleMessage || !visibleMessage.id) {
    return null;
  }

  const dismiss = () => {
    setDismissedIds((current) => (current.includes(visibleMessage.id!) ? current : [...current, visibleMessage.id!]));
  };

  return (
    <div
      className="border-b border-surface-outline bg-yellow-50 text-yellow-950"
      data-test-id="site-message-banner"
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-7xl items-start justify-between gap-4 px-4 py-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="min-w-0 flex-1 text-sm leading-6 text-yellow-950">
            <span className="font-semibold text-black opacity-95">{visibleMessage.title}:</span>{" "}
            <MessageDescription description={visibleMessage.description} />
          </div>
        </div>

        <button
          type="button"
          className="mt-0.5 shrink-0 rounded-md p-1 text-yellow-700 transition-colors hover:bg-yellow-100 hover:text-yellow-900"
          data-test-id="site-message-banner-dismiss"
          aria-label="Dismiss message"
          onClick={dismiss}
        >
          <IconX size={18} />
        </button>
      </div>
    </div>
  );
}
